import { createSlice } from "@reduxjs/toolkit";
import { AppConfig } from "../../types";
import { fetchConfig } from "../config/ConfigActions";
import { RMAnalytics } from "./RMAnalytics";
import {
  ANALYTICS_TRACK_FLIGHT_DETAILS,
  ANALYTICS_TRACK_FLIGHT_DETAILS_PRINT,
  ANALYTICS_TRACK_FLIGHT_LIST_PRINT,
  ANALYTICS_TRACK_MAP_LOADED,
  ANALYTICS_TRACK_PAGE_VIEW,
  ANALYTICS_TRACK_FIND_DESTINATIONS,
  ANALYTICS_TRACK_FIND_AIRPORTS_SERVED,
  ANALYTICS_TRACK_FLIGHT_BOOKING_LINK,
} from "./AnalyticsActions";

const ANALYTICS_ID_DEV = "G-N1E8C6K9Y9";
const ANALYTICS_ID_DEMO = "G-F8FX22VK27";
const ANALYTICS_ID_PRODUCTION = "G-LGBN8BX8MC";

export const AnalyticsSlice = createSlice({
  name: "analytics",
  initialState: {
    analytics: null,
  },
  reducers: {},
  extraReducers: {
    [fetchConfig.fulfilled.toString()]: (state, action) => {
      if (!action.payload.AppConfigCustom.disableGATracking) {
        state.analytics = setupGtm(action.payload.AppConfigCustom);
      }
    },
    [ANALYTICS_TRACK_MAP_LOADED]: (state, action) => {
      if (state.analytics) {
        const mapId = action.payload.mapId;
        const tileSourceId = action.payload.tileSource.id;
        const eventAction = mapId + "_tiles_loaded";
        state.analytics.recordEvent(
          { category: RMAnalytics.EventCategory.MAP, action: eventAction },
          tileSourceId,
          { value: 1 }
        );
      }
    },
    "searchFlights/fetchResults/fulfilled": (state, action) => {
      // For recording the query event, prefer the request for outbound-connections over outbound-directs.
      // Queries with via airports only appear in the connections request, not the direct one.
      const { queryTrigger, values } = action.payload;
      let value = values.find(
        (value) =>
          value.type === "searchFlights/fetchOutboundConnections/fulfilled"
      );
      if (!value) {
        value = values.find(
          (value) =>
            value.type === "searchFlights/fetchOutboundDirects/fulfilled"
        );
      }
      if (state.analytics && value) {
        const analyticsObj =
          state.analytics.createAnalyticsObjectFromCbwsRequest(
            value.meta.arg.form
          );
        state.analytics.recordEvent(
          RMAnalytics.Event.FIND_FLIGHTS,
          queryTrigger,
          analyticsObj
        );
        state.analytics.recordPageView(
          RMAnalytics.PageView.SEARCH_RESULTS,
          analyticsObj
        );
      }
    },
    "searchFlights/resetResults": (state, action) => {
      if (state.analytics) {
        state.analytics.recordPageView(RMAnalytics.PageView.START);
      }
    },
    [ANALYTICS_TRACK_FLIGHT_DETAILS]: (state, action) => {
      if (state.analytics) {
        const analyticsObj = state.analytics.createAnalyticsObjectFromFlight(
          action.payload
        );

        state.analytics.recordPageView(
          RMAnalytics.PageView.FLIGHT_DETAIL,
          analyticsObj
        );
      }
    },
    [ANALYTICS_TRACK_PAGE_VIEW]: (state, action) => {
      if (state.analytics) {
        state.analytics.recordPageView(action.payload);
      }
    },
    [ANALYTICS_TRACK_FLIGHT_DETAILS_PRINT]: (state, action) => {
      if (state.analytics) {
        state.analytics.recordEvent(RMAnalytics.Event.PRINT_FLIGHT_DETAIL);
      }
    },
    [ANALYTICS_TRACK_FLIGHT_LIST_PRINT]: (state, action) => {
      if (state.analytics) {
        state.analytics.recordEvent(RMAnalytics.Event.PRINT_FLIGHT_LIST);
      }
    },
    [ANALYTICS_TRACK_FIND_DESTINATIONS]: (state, action) => {
      if (state.analytics) {
        const { queryTrigger, eventRequestUse } = action.payload;
        const analyticsObj = state.analytics.createAnalyticsObjectFromCbwsRequest(eventRequestUse);
        state.analytics.recordEvent(
          RMAnalytics.Event.FIND_DESTINATIONS,
          queryTrigger,
          analyticsObj
        );
      }
    },
    [ANALYTICS_TRACK_FIND_AIRPORTS_SERVED]: (state, action) => {
      if (state.analytics) {
        const { queryTrigger, request } = action.payload;
        const analyticsObj = state.analytics.createAnalyticsObjectFromCbwsRequest(request);
        state.analytics.recordEvent(
          RMAnalytics.Event.FIND_AIRPORTS_SERVED,
          queryTrigger,
          analyticsObj
        );
      }
    },
    [ANALYTICS_TRACK_FLIGHT_BOOKING_LINK]: (state, action)=> {
      if (state.analytics) {
        state.analytics.recordEvent(RMAnalytics.Event.BOOKING_FLIGHT_LINK, "Flight Booking Link", action.payload);
      }
    }
  },
});

function setupGtm(appConfig: AppConfig) {
  const isAirline = appConfig.customerType.airline;
  const custId = appConfig.customerId;
  const toolId = "42001"; // TODO: retrieve this somewhere
  const isTimeTable = appConfig.lfMain.substring(0, 3) === "ITT";
  const gaTrackingConfigList = [{ id: `${defineAnalyticsId()}` }];

  if (appConfig.gaCustomConfig) {
    if (appConfig.gaCustomConfig instanceof Array) {
      gaTrackingConfigList.push(...appConfig.gaCustomConfig)
    } else {
      gaTrackingConfigList.push(appConfig.gaCustomConfig);
    }
  }

  var _analytics = new RMAnalytics(gaTrackingConfigList);

  var gaSetupId = `${custId.toLowerCase()}_${toolId.toLowerCase()}`;
  var gaSetupType = isAirline ? "airline" : "airport";
  if (isTimeTable) gaSetupType = "itt";
  _analytics.setup(gaSetupId, gaSetupType);
  return _analytics;
}

function defineAnalyticsId() {
  const hostname = window.location.hostname;
  if (
    hostname === "tracker.flightview.com" ||
    hostname === "stracker.flightview.com"
  ) {
    return ANALYTICS_ID_PRODUCTION;
  } else if (
    hostname === "ttracker.flightview.com" ||
    hostname === "demotracker.flightview.com"
  ) {
    return ANALYTICS_ID_DEMO;
  } else {
    return ANALYTICS_ID_DEV;
  }
}

export default AnalyticsSlice.reducer;
